/*
 * Description:
 * ---------------------
 * Threat Intelligence, for super admins: what every box on this control plane
 * blocks besides its own bans (server/pkg/fleetbans). Whether bans are shared
 * across the fleet at all; Weavian's AI, the Isolation Forest that finds slow
 * attacks spread across sites, with what it scored last; what is shared now;
 * and the crowdsourced lists, AbuseIPDB and a CrowdSec integration, with
 * their keys (sealed on the server, shown only by their last characters),
 * whether each is pulled, and whether the fleet's bans are reported to
 * AbuseIPDB. Wording in intrusionWords.js.
 *
 * ! file components/ThreatIntelPanel.js
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { AiIcon } from "./AiIcon";
import { useConfirm } from "./ConfirmModal";
import { count, plural } from "./firewallWords";
import { INTEL as T } from "./intrusionWords";
import "./Firewall.css";

const at = (iso) => (iso ? new Date(iso).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }) : "");

function Switch({ on, label, disabled, onChange }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={label} className="fw-ip-switch" disabled={disabled}
      onClick={() => onChange(!on)}><span /></button>
  );
}

function AbuseIPDB({ set, status, busy, put }) {
  const [key, setKey] = useState("");
  const a = set.abuseipdb || {};
  return (
    <section className="fw-card">
      <header className="fw-card-head">
        <h3 className="fw-card-title">{T.abuse}</h3>
        <p>{T.abuseMeans}</p>
      </header>
      <div className="fw-ip-form">
        <label>{T.key}
          {a.key_ending
            ? <span className="fw-ti-kept">{T.keyEnding(a.key_ending)}{a.added_by ? `, added by ${a.added_by} ${at(a.added_at)}` : ""}</span>
            : <input className="fw-input" value={key} onChange={(e) => setKey(e.target.value)} placeholder={T.keyNew} autoComplete="off" />}
        </label>
        <label>{T.confidence}
          <select className="fw-input" value={a.confidence || 100} disabled={busy || !a.key_ending} onChange={(e) => put({ abuseipdb: { confidence: Number(e.target.value) } })}>
            {[100, 90, 75, 50, 25].map((n) => <option key={n} value={n}>{n}%</option>)}
          </select>
        </label>
      </div>
      <div className="fw-ti-switches">
        <label><Switch on={a.pull} label={T.pull} disabled={busy || !a.key_ending} onChange={(v) => put({ abuseipdb: { pull: v } })} /> {T.pull}</label>
        <label><Switch on={a.report} label={T.report} disabled={busy || !a.key_ending} onChange={(v) => put({ abuseipdb: { report: v } })} /> {T.report}</label>
      </div>
      <p className="fw-muted">{T.status(status)}</p>
      <div className="fw-ip-form-acts">
        {a.key_ending
          ? <button type="button" className="fw-btn is-danger-quiet" disabled={busy} onClick={() => put({ abuseipdb: { key: "" } })}>{T.remove}</button>
          : <button type="button" className="fw-btn is-primary" disabled={busy || !key.trim()} onClick={() => put({ abuseipdb: { key: key.trim() } }).then((ok) => ok && setKey(""))}>{T.save}</button>}
      </div>
    </section>
  );
}

function CrowdSec({ set, status, busy, put }) {
  const c = set.crowdsec || {};
  const [url, setUrl] = useState(c.url || "");
  const [username, setUsername] = useState(c.username || "");
  const [password, setPassword] = useState("");
  const changed = url.trim() !== (c.url || "") || username.trim() !== (c.username || "") || password !== "";
  const save = () => put({ crowdsec: { url: url.trim(), username: username.trim(), ...(password ? { password } : {}) } }).then((ok) => ok && setPassword(""));
  return (
    <section className="fw-card">
      <header className="fw-card-head">
        <h3 className="fw-card-title">{T.crowd}</h3>
        <p>{T.crowdMeans}</p>
      </header>
      <div className="fw-ip-form">
        <label className="is-wide">{T.url}<input className="fw-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://admin.api.crowdsec.net/v1/integrations/…/content" /></label>
        <label>{T.username}<input className="fw-input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" /></label>
        <label>{T.password}<input className="fw-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={c.url ? T.passwordKept : ""} autoComplete="new-password" /></label>
      </div>
      <div className="fw-ti-switches">
        <label><Switch on={c.pull} label={T.pull} disabled={busy || !c.url} onChange={(v) => put({ crowdsec: { pull: v } })} /> {T.pull}</label>
      </div>
      <p className="fw-muted">{T.status(status)}</p>
      <div className="fw-ip-form-acts">
        {c.url && <button type="button" className="fw-btn is-danger-quiet" disabled={busy} onClick={() => put({ crowdsec: { url: "" } })}>{T.remove}</button>}
        <button type="button" className="fw-btn is-primary" disabled={busy || !changed || !url.trim()} onClick={save}>{T.save}</button>
      </div>
    </section>
  );
}

function Model({ model, features }) {
  const top = model?.top || [];
  return (
    <section className="fw-card">
      <header className="fw-card-head">
        <h3 className="fw-card-title"><AiIcon size={18} /> {T.model}</h3>
        <p>{T.modelMeans}</p>
        <p className="fw-lead">{!model?.at ? T.modelWaiting("it has not run yet") : model.waiting ? T.modelWaiting(model.waiting) : T.modelLast(model)}</p>
      </header>
      {top.length > 0 && (
        <details>
          <summary>{T.top}</summary>
          <div className="fw-log-wrap">
            <table className="fw-log cards-on-phone">
              <thead><tr>{Object.values(T.topCols).map((c) => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>
                {top.map((s) => (
                  <tr key={s.address}>
                    <td><code>{s.address}</code></td>
                    <td data-label={T.topCols.score}>{Math.round(s.score * 100)}%</td>
                    <td data-label={T.topCols.sites}>{s.sites}</td>
                    <td data-label={T.topCols.days}>{s.days}</td>
                    <td data-label={T.topCols.listed}>{s.listed ? <span className="fw-why is-bad">Listed</span> : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
      {features?.length > 0 && <p className="fw-muted">{T.features}: {features.join(", ")}.</p>}
    </section>
  );
}

function Shared({ fleet, shared }) {
  const src = fleet?.sources || {};
  const feeds = fleet?.feeds?.sources || {};
  return (
    <section className="fw-card">
      <header className="fw-card-head">
        <h3 className="fw-card-title">{T.shared}</h3>
        <p className="fw-lead">
          {plural(src.fleet || 0, "address", "addresses")} banned at sites, {plural(src.model || 0, "address", "addresses")} from the AI,
          {" "}{count(feeds.abuseipdb || 0)} from AbuseIPDB and {count(feeds.crowdsec || 0)} from CrowdSec.
        </p>
      </header>
      {(shared || []).length === 0 ? <p className="fw-muted">{T.sharedNone}</p> : (
        <div className="fw-log-wrap">
          <table className="fw-log cards-on-phone">
            <thead><tr>{Object.values(T.sharedCols).map((c) => <th key={c}>{c}</th>)}</tr></thead>
            <tbody>
              {shared.map((e) => (
                <tr key={e.net}>
                  <td><code>{e.net}</code></td>
                  <td data-label={T.sharedCols.from}>{e.source === "model" ? <><AiIcon size={14} /> AI</> : "Bans"}</td>
                  <td data-label={T.sharedCols.sites}>{e.sites || ""}</td>
                  <td data-label={T.sharedCols.until}>{at(e.until)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function ThreatIntelPanel({ serverApiUrl }) {
  const [view, setView] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const read = useCallback(async () => {
    const res = await fetchWithTimeout(serverApiUrl("/threatintel"), { credentials: "same-origin" }).catch(() => null);
    if (!res) {
      setError("The Weavian server did not answer.");
      return;
    }
    const out = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(out.error || `The server refused (HTTP ${res.status}).`);
      return;
    }
    setError("");
    setView(out);
  }, [serverApiUrl]);
  useEffect(() => { read(); }, [read]);

  const put = async (change) => {
    setBusy(true);
    setError("");
    const res = await fetchWithTimeout(serverApiUrl("/threatintel"), {
      method: "PUT", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(change),
    }, 30000).catch(() => null);
    const out = res ? await res.json().catch(() => ({})) : {};
    setBusy(false);
    if (!res?.ok) {
      setError(out.error || "The Weavian server did not answer.");
      return false;
    }
    setView(out);
    return true;
  };
  const share = async (on) => {
    if (!on && !(await confirm({ ...T.offAsk, variant: "danger" }))) return;
    put({ off: !on });
  };

  if (!view) return <div className="fw"><div className="fw-card"><p className="fw-muted">{error || "Reading…"}</p></div></div>;
  const off = !!view.fleet?.off;
  return (
    <div className="fw">
      {error && <div className="fw-notice is-bad" role="alert"><FiAlertTriangle size={16} /> <span>{error}</span></div>}
      {view.unreadable && <div className="fw-notice is-warn" role="note"><FiAlertTriangle size={16} /> <span>{T.unreadable(view.unreadable)}</span></div>}
      <section className="fw-card">
        <header className="fw-card-head is-split">
          <div>
            <h3 className="fw-card-title">{T.sharing}</h3>
            <p className="fw-lead">{off ? T.sharingOff : T.sharingOn}</p>
          </div>
          <Switch on={!off} label={T.sharing} disabled={busy} onChange={share} />
        </header>
      </section>
      <Model model={view.model} features={view.features} />
      <Shared fleet={view.fleet} shared={view.shared} />
      <AbuseIPDB set={view.settings || {}} status={view.status?.abuseipdb} busy={busy} put={put} />
      <CrowdSec key={view.settings?.crowdsec?.url || "none"} set={view.settings || {}} status={view.status?.crowdsec} busy={busy} put={put} />
    </div>
  );
}
